import { hydrateRunUpdate, type RunUpdate } from "../shared/hub-capture.ts";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError, api, makeCommand, runEventsPath } from "./api.ts";
import type { Command, LabAction, RunView } from "../shared/types.ts";

/** `ended`: the server refused this view (signed out, revoked or expired); automatic retries stop. */
export type Connection = "connecting" | "live" | "offline" | "ended";

/** A rejected or unconfirmed command whose Retry resends that exact envelope. */
export interface CommandFailure {
  message: string;
  /** No definite answer arrived, so the action may already have been applied. */
  uncertain: boolean;
  retry: () => void;
}

interface Failed {
  command: Command;
  /** Run generation the command belongs to. */
  run: number;
  /** Dispatch order; only a later command supersedes this failure. */
  seq: number;
  message: string;
  uncertain: boolean;
  onCommit?: () => void;
}

const RETRY_BASE_MS = 1000,
  RETRY_MAX_MS = 30000;
const definitive = (e: unknown) =>
  e instanceof ApiError && [401, 403, 410].includes(e.status);
const messageOf = (e: unknown) =>
  e instanceof Error && e.message
    ? e.message
    : "The request could not be completed.";

const position = (v: RunView) =>
  v.role === "presenter"
    ? { id: v.state.id, revision: v.state.revision }
    : { id: v.runId, revision: v.revision };

export function useRun(runId: string | null, participant = false) {
  const [view, setView] = useState<RunView | null>(null),
    [problem, setProblem] = useState(""),
    [failed, setFailed] = useState<Failed | null>(null),
    [connection, setConnection] = useState<Connection>("connecting"),
    [pending, setPending] = useState(0),
    [attempt, setAttempt] = useState(0);
  const current = useRef<RunView | null>(null),
    queue = useRef(Promise.resolve()),
    // The run generation changes only when another run opens; reconnecting keeps it.
    run = useRef(0),
    stream = useRef(0),
    sequence = useRef(0),
    failure = useRef<Failed | null>(null),
    backoff = useRef(0),
    ended = useRef(false),
    opened = useRef<string | null>(null);
  const key = runId === null ? null : (participant ? "p:" : "r:") + runId;
  const accept = useCallback((update: RunUpdate) => {
    const next = hydrateRunUpdate(update, current.current);
    const before = current.current && position(current.current),
      after = position(next);
    if (
      !before ||
      after.id !== before.id ||
      after.revision >= before.revision
    ) {
      current.current = next;
      setView(next);
    }
  }, []);
  const showFailure = useCallback((next: Failed | null) => {
    failure.current = next;
    setFailed(next);
  }, []);

  useEffect(() => {
    if (opened.current !== key) {
      opened.current = key;
      run.current++;
      current.current = null;
      setView(null);
      setProblem("");
      showFailure(null);
      backoff.current = 0;
      ended.current = false;
      setConnection("connecting");
    }
    if (!runId) return;
    const epoch = ++stream.current,
      active = () => stream.current === epoch;
    let source: EventSource | null = null,
      timer: number | undefined;
    // A closed stream is reopened with capped, jittered backoff after re-reading canonical state.
    const later = () => {
      if (!active() || ended.current) return;
      const delay = Math.min(
        RETRY_MAX_MS,
        RETRY_BASE_MS * 2 ** backoff.current,
      );
      backoff.current = Math.min(backoff.current + 1, 6);
      timer = window.setTimeout(
        () => {
          if (active()) setAttempt((n) => n + 1);
        },
        delay * (0.8 + Math.random() * 0.4),
      );
    };
    const open = () => {
      const events = new EventSource(runEventsPath(runId, participant));
      source = events;
      events.onmessage = (event) => {
        if (!active()) return;
        let next: RunView;
        try {
          next = hydrateRunUpdate(JSON.parse(event.data), current.current);
        } catch {
          setProblem("The run update could not be read. Reconnecting.");
          setConnection("offline");
          events.close();
          later();
          return;
        }
        accept(next);
        setProblem("");
        setConnection("live");
      };
      events.onopen = () => {
        if (!active()) return;
        backoff.current = 0;
        setConnection("live");
      };
      // Browsers retry only network drops; a non-200 reply closes the stream for good.
      events.onerror = () => {
        if (!active()) return;
        setConnection("offline");
        if (events.readyState === EventSource.CLOSED) {
          events.close();
          later();
        }
      };
    };
    (participant ? api.participantView(runId) : api.view(runId))
      .then((v) => {
        if (!active()) return;
        accept(v);
        setProblem("");
        open();
      })
      .catch((e) => {
        if (!active()) return;
        if (definitive(e)) {
          ended.current = true;
          setConnection("ended");
          setProblem(messageOf(e));
          return;
        }
        // With a view on screen the connection status explains the outage.
        if (!current.current) setProblem(messageOf(e));
        setConnection("offline");
        later();
      });
    return () => {
      stream.current++;
      window.clearTimeout(timer);
      source?.close();
    };
  }, [key, attempt, accept, showFailure]);

  const send = useCallback(
    (
      command: Command,
      generation: number,
      seq: number,
      report: boolean,
      onCommit?: () => void,
    ): Promise<void> => {
      if (!runId)
        return Promise.reject(new Error("Open a run before taking an action."));
      setPending((n) => n + 1);
      const operation = queue.current
        .catch(() => {})
        .then(async () => {
          if (run.current !== generation)
            throw new Error(
              "The open run changed. Retry the action in the current run.",
            );
          const next = await api.command(runId, command, participant);
          if (run.current !== generation) return;
          accept(next);
          // Its own retry, or a later Hub or lab action, supersedes a failure; participant input does not.
          const shown = failure.current;
          if (
            shown &&
            (shown.command.id === command.id || (report && shown.seq < seq))
          )
            showFailure(null);
          onCommit?.();
        })
        .catch((e) => {
          const shown = failure.current;
          if (
            report &&
            run.current === generation &&
            (!shown || shown.seq <= seq)
          ) {
            const uncertain =
              e instanceof ApiError && (e.status === 0 || e.status >= 500);
            showFailure({
              command,
              run: generation,
              seq,
              onCommit,
              uncertain,
              message: uncertain
                ? "The lab did not confirm this action, so it may already have been applied. Retry resends the same action, which cannot be applied twice."
                : messageOf(e),
            });
          }
          throw e;
        })
        .finally(() => setPending((n) => Math.max(0, n - 1)));
      queue.current = operation;
      return operation;
    },
    [runId, participant, accept, showFailure],
  );

  /** Participant input. The renderer shows its own failures in context. */
  const dispatch = useCallback(
    (action: LabAction, capturedControlRevision?: number): Promise<void> => {
      const controlRevision =
        capturedControlRevision ??
        (current.current?.role === "participant"
          ? current.current.session.controller.revision
          : undefined);
      return send(
        makeCommand(action, controlRevision),
        run.current,
        ++sequence.current,
        false,
      );
    },
    [send],
  );
  /**
   * Hub and lab actions. A failure stays on screen with its exact command until it is retried,
   * dismissed or superseded; `onCommit` runs once when that command commits, on the first
   * attempt or an explicit Retry, even if the caller has unmounted.
   */
  const act = useCallback(
    (action: LabAction, onCommit?: () => void): Promise<boolean> =>
      send(
        makeCommand(action),
        run.current,
        ++sequence.current,
        true,
        onCommit,
      ).then(
        () => true,
        () => false,
      ),
    [send],
  );
  const retry = useCallback(
    (target: Failed) => {
      if (failure.current !== target || target.run !== run.current) return;
      showFailure(null);
      void send(
        target.command,
        target.run,
        ++sequence.current,
        true,
        target.onCommit,
      ).catch(() => {});
    },
    [send, showFailure],
  );
  const dismiss = useCallback(() => {
    showFailure(null);
    setProblem("");
  }, [showFailure]);
  /** Reopens the stream and re-reads canonical state; local screens and drafts stay mounted. */
  const reconnect = useCallback(() => {
    ended.current = false;
    backoff.current = 0;
    setConnection("connecting");
    setAttempt((n) => n + 1);
  }, []);
  const commandFailure = useMemo<CommandFailure | null>(
    () =>
      failed && {
        message: failed.message,
        uncertain: failed.uncertain,
        retry: () => retry(failed),
      },
    [failed, retry],
  );
  return {
    view,
    error: failed?.message || problem,
    problem,
    failure: commandFailure,
    dismiss,
    connection,
    busy: pending > 0,
    dispatch,
    act,
    reconnect,
  };
}
